import PropTypes from 'prop-types'
import { startCase } from 'lodash'
import { useTheme } from '@emotion/react'
import { text } from 'chaoskit/src/assets/styles/utility'
import { Loader, Alert } from 'chaoskit/src/components'
import { useQuery } from 'react-query'

import Icon from './Icon'
import TextLink from './TextLink'

import { shadowEmboss } from '~styles/utility'

// Based on https://github.com/gremlin/service/blob/0092548c606c0864084b56ec9b2c433c6ba30331/libraries/core/src/main/java/com/gremlininc/model/scenario/GraphNode.java#L22
const scenarioGraphTypes = {
  InfraAttack: 'InfraAttack',
  Delay: 'Delay',
  SynchronousStatusCheck: 'SynchronousStatusCheck',
  Concurrent: 'Concurrent',
  ContinuousStatusCheck: 'ContinuousStatusCheck',
}

const isGraphNodeType = (node, type) => node?.type === type

const isDelayNode = (node) => isGraphNodeType(node, scenarioGraphTypes.Delay)

const isConcurrentNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.Concurrent)

// eslint-disable-next-line func-names
const walkGraph = function* (graph, start = graph?.start_id) {
  if (!start) return []

  let next = start

  while (next && graph.nodes[next]) {
    const current = graph.nodes[next]
    next = current?.next
    yield current
  }
}

const arrayFromGraph = (graph) => {
  const arrayFromWalkGraph = Array.from(walkGraph(graph)).map((node) => {
    if (isConcurrentNode(node)) {
      return {
        ...node,
        branches: node.branches.map(arrayFromGraph),
      }
    }
    return node
  })

  if (isDelayNode(arrayFromWalkGraph[arrayFromWalkGraph.length - 1])) {
    arrayFromWalkGraph.splice(-1, 1)
  }

  return arrayFromWalkGraph
}

const mapBranchedSteps = (steps, parent) =>
  steps.flatMap((step) => {
    if (step?.branches?.length) {
      return [
        step,
        ...step.branches.flatMap((branch, index) =>
          mapBranchedSteps(branch, {
            ...step,
            branchId: `${step.id}-${index}`, // use the step id as well as the index of the branch
          })
        ),
      ]
    }
    if (parent) {
      return {
        ...step,
        branchId: parent.branchId,
        parent,
      }
    }

    return step
  })

const getGraphNodesByType = (graph, ...types) => {
  const typeSet = new Set(types)

  return mapBranchedSteps(arrayFromGraph(graph)).filter((node) =>
    typeSet.has(node.type)
  )
}

function getScenarioCommands(scenario) {
  return Array.from(
    new Set(
      getGraphNodesByType(scenario.graph, scenarioGraphTypes.InfraAttack).map(
        (node) => node.impact_definition.infra_command_args.cli_args[0]
      )
    )
  )
}

function getUniqueScenarioCommands(scenario) {
  const statusChecks = getGraphNodesByType(
    scenario.graph,
    scenarioGraphTypes.SynchronousStatusCheck,
    scenarioGraphTypes.ContinuousStatusCheck
  ).map(() => 'status_check')

  const commands = getScenarioCommands(scenario)

  return Array.from(new Set([...statusChecks, ...commands]))
}

const countableNodeTypes = [
  scenarioGraphTypes.InfraAttack,
  scenarioGraphTypes.SynchronousStatusCheck,
]

function getScenarioStepCount(scenario) {
  const countableNodes = getGraphNodesByType(
    scenario.graph,
    ...countableNodeTypes
  )
  return countableNodes.length
}

// Used to space things out in context by default
const ExperimentCardsWrapper = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{ marginTop: theme.space.large, marginBottom: theme.space.large }}
      {...props}
    />
  )
}

const ScenarioAttribute = ({ label, value }) => {
  const theme = useTheme()

  return (
    <div>
      <div
        css={{
          ...theme.text.xsmall,
          color: theme.fontColor.muted,
          marginBottom: theme.space.xsmall,
        }}
      >
        {label}:
      </div>
      <div
        css={{
          fontWeight: theme.fontWeight.bold,
          ...theme.text.small,
        }}
      >
        {value}
      </div>
    </div>
  )
}

ScenarioAttribute.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
}

const getFormattedAttackType = (type = '-') => {
  switch (type) {
    case 'cpu':
    case 'CPU':
      return 'CPU'
    case 'io':
    case 'IO':
      return 'IO'
    case 'dns':
    case 'DNS':
      return 'DNS'
    default: {
      return startCase(type)
    }
  }
}

export const ExperimentCardStatic = ({ recommendedScenario }) => {
  const theme = useTheme()

  const steps = getScenarioStepCount(recommendedScenario)

  const uniqueCommands = getUniqueScenarioCommands(recommendedScenario)

  return (
    <article
      css={[
        shadowEmboss(theme),
        {
          borderRadius: theme.borderRadius.base,
          background: theme.color.light.base,
          padding: theme.space.base,
          display: 'grid',
          gridTemplateRows: '1fr auto',
          gap: theme.space.medium,
          maxWidth: 300, // For standalone cards that appear in tutorials; may be reworked within a grid-system in the future
        },
      ]}
    >
      <header
        css={{
          display: 'grid',
          gap: theme.space.base,
          gridTemplateRows: recommendedScenario.hypothesis ? 'auto 1fr' : null,
        }}
      >
        <div
          css={{
            ...theme.text.medium,
            fontWeight: theme.fontWeight.bold,
          }}
        >
          {recommendedScenario.name}
        </div>
        {recommendedScenario.hypothesis && (
          <div
            css={{
              ...theme.text.small,
            }}
          >
            {recommendedScenario.hypothesis}
          </div>
        )}
      </header>
      <footer
        css={{
          display: 'grid',
          gap: theme.space.base,
        }}
      >
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: theme.space.base,
          }}
        >
          <ScenarioAttribute
            label="Length"
            value={`${steps} step${steps === 1 ? '' : 's'}`}
          />
          <ScenarioAttribute
            label={`Attack type${uniqueCommands.length === 1 ? '' : 's'}`}
            value={uniqueCommands.map(getFormattedAttackType).join(', ')}
          />
        </div>
        <div>
          <TextLink
            href={`https://app.gremlin.com/scenarios/recommended/${recommendedScenario.static_endpoint_name}`}
            css={{
              display: 'inline-grid',
              gap: theme.space.small,
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              gridAutoFlow: 'column',
              alignItems: 'center',
            }}
          >
            <Icon icon="external" css={{ color: theme.fontColor.muted }} />
            <span
              css={[
                text.underline,
                {
                  ...theme.text.small,
                  ...text.expanded(theme),
                  fontWeight: theme.fontWeight.bold,
                },
              ]}
            >
              Run Scenario
            </span>
          </TextLink>
        </div>
      </footer>
    </article>
  )
}

ExperimentCardStatic.propTypes = {
  recommendedScenario: PropTypes.object.isRequired,
}

export const ExperimentCard = ({ staticEndpointName }) => {
  const theme = useTheme()

  const { isLoading, error, data } = useQuery('recommendedScenarios', () =>
    fetch('https://api.gremlin.com/v1/scenarios/recommended').then((res) =>
      res.json()
    )
  )

  if (isLoading) {
    return (
      <ExperimentCardsWrapper>
        <Loader
          css={{
            ...theme.text.large,
            color: theme.color.primary.base,
          }}
        />
      </ExperimentCardsWrapper>
    )
  }

  if ((!isLoading && data.length === 0) || error) {
    return (
      <ExperimentCardsWrapper>
        <Alert type="danger">Recommended Scenario not found</Alert>
      </ExperimentCardsWrapper>
    )
  }

  const chosenRecommendedScenario = data.find(
    (recommendedScenario) =>
      staticEndpointName === recommendedScenario.static_endpoint_name
  )

  if (!chosenRecommendedScenario) {
    return (
      <ExperimentCardsWrapper>
        <Alert type="danger">Recommended Scenario not found</Alert>
      </ExperimentCardsWrapper>
    )
  }

  return (
    <ExperimentCardsWrapper>
      <ExperimentCardStatic recommendedScenario={chosenRecommendedScenario} />
    </ExperimentCardsWrapper>
  )
}

ExperimentCard.propTypes = {
  staticEndpointName: PropTypes.string.isRequired,
}
